'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import { useGate } from './gate/GateProvider'
import { COMMAND_DECORATORS } from './marketplace-tokens'
import type { MarketplaceCommand } from '@/lib/marketplace/queries'

// Split a command name across two lines on the first space when there is
// one — matches the mock's "Fraud / Sweep" stacked title. Single-word
// names render on one line.
function splitTitle(name: string): [string, string?] {
  const trimmed = name.trim()
  const space = trimmed.indexOf(' ')
  if (space === -1) return [trimmed]
  return [trimmed.slice(0, space), trimmed.slice(space + 1)]
}

export function CommandCard({ command }: { command: MarketplaceCommand }) {
  const { requireAuth } = useGate()
  const decorator = COMMAND_DECORATORS[command.category]
  const p = decorator.palette
  const [line1, line2] = splitTitle(command.name)
  // The Negative-Keyword-Audit palette has white ink on a dark purple bg
  // and uses a different ink for the background glyph; the others use the
  // same ink for both. We key the glyph stronger for those two per spec.
  const glyphColor = p.cta
  const glyphOpacity =
    command.category === 'optimization' || command.category === 'intelligence' ? 0.18 : 0.15

  return (
    <button
      type="button"
      onClick={() => requireAuth()}
      style={{ background: p.bg }}
      className="group relative flex h-[172px] w-[200px] flex-none flex-col justify-between overflow-hidden rounded-[14px] p-[18px] text-left transition-transform duration-200 ease-out [scroll-snap-align:start] hover:-translate-y-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
    >
      <span
        aria-hidden
        className="absolute -bottom-[30px] -right-[30px]"
        style={{ color: glyphColor, opacity: glyphOpacity }}
      >
        <HugeiconsIcon icon={decorator.icon} className="size-[120px]" />
      </span>
      <div className="relative z-[2]">
        <div
          className="mb-2 font-mono text-[11px] uppercase tracking-[0.08em]"
          style={{ color: p.ink }}
        >
          {decorator.eyebrow}
        </div>
        <div
          className="text-xl font-bold leading-[1.15] tracking-[-0.015em]"
          style={{ color: p.ink }}
        >
          {line1}
          {line2 && (
            <>
              <br />
              {line2}
            </>
          )}
        </div>
      </div>
      <span
        className="relative z-[2] inline-flex items-center self-start rounded-lg px-3.5 py-2 text-xs font-semibold text-white"
        style={{ background: p.cta }}
      >
        Install
      </span>
    </button>
  )
}
